import { useState } from 'react'
import type { View } from './derive'
import { reachText } from './derive'
import { Avatar, Chip } from './parts'
import { HiddenPeople } from './HiddenPeople'
import { NotInKronos } from './NotInKronos'
import { PersonPanel } from './PersonPanel'

type Filter = 'all' | 'online' | 'ending' | 'none'

/** Everyone: their teams, where they are signed in, and how much they can reach. */
export function PeopleTab({ view }: { view: View }): JSX.Element {
  const [q, setQ] = useState('')
  const [filter, setFilter] = useState<Filter>('all')
  const [open, setOpen] = useState<string | null>(null)
  const needle = q.trim().toLowerCase()
  const teamNames = (ids: readonly string[]) => ids.map((id) => view.team(id)?.name ?? '').join(' ').toLowerCase()
  const shown = view.people
    .filter((p) => filter === 'all' || (filter === 'online' && p.online) || (filter === 'ending' && p.rules.some((r) => r.expiresAt)) || (filter === 'none' && p.teamIds.length === 0))
    .filter((p) => !needle || p.name.toLowerCase().includes(needle) || p.email.toLowerCase().includes(needle) || teamNames(p.teamIds).includes(needle))
  const person = open ? view.person(open) : undefined

  return (
    <div className="stack" style={{ gap: 14 }}>
      <NotInKronos view={view} />
      <div className="cx-toolbar">
        <input className="cx-search" aria-label="Find a person" placeholder="Find a person or a team" value={q} onChange={(e) => setQ(e.currentTarget.value)} />
        <div className="segmented" role="tablist" aria-label="Show">
          {([['all', 'Everyone'], ['online', 'Online now'], ['ending', 'Has access that ends'], ['none', 'In no team']] as const).map(([id, label]) => (
            <button key={id} role="tab" aria-selected={filter === id} onClick={() => setFilter(id)}>{label}</button>
          ))}
        </div>
      </div>
      <div className="cx-columns">
        <section className="cx-main cx-panel" aria-label="People">
          <div className="cx-scroll">
            <div className="cx-table cx-table--people" role="table" aria-label="People">
              <div className="cx-table__row cx-table__row--head" role="row">
                <span role="columnheader">Person</span>
                <span role="columnheader">Teams</span>
                <span role="columnheader">Online on</span>
                <span role="columnheader" className="cx-right">Can reach</span>
              </div>
              <div className="cx-table__body">
                {shown.map((p) => {
                  const on = p.devices.find((d) => d.online)
                  return (
                    <button key={p.id} role="row" className={`cx-table__row cx-table__row--button ${open === p.id ? 'is-open' : ''}`} onClick={() => setOpen(p.id)}>
                      <span role="cell" className="row" style={{ gap: 10, minWidth: 0 }}>
                        <Avatar initials={p.initials} online={p.online} />
                        <span className="stack" style={{ gap: 0, minWidth: 0 }}>
                          <span className="cx-strong">{p.name}</span>
                          <span className="muted cx-small cx-ellipsis">{p.email}</span>
                        </span>
                      </span>
                      <span role="cell" className="cx-chips">
                        {p.teamIds.map((id) => (
                          <Chip key={id}>{view.team(id)?.name}</Chip>
                        ))}
                        {p.rules.length ? <Chip tone="plain">{p.rules.length} of their own</Chip> : null}
                        {p.teamIds.length === 0 && p.rules.length === 0 ? <span className="muted cx-small">No access</span> : null}
                      </span>
                      <span role="cell" className={on ? 'cx-net is-online' : 'muted'}>
                        {on ? on.name : p.devices.length ? 'Offline' : 'Not signed in'}
                      </span>
                      <span role="cell" className="cx-right cx-mono cx-strong">{reachText(p.robots, p.computers)}</span>
                    </button>
                  )
                })}
                {shown.length === 0 ? <p className="muted cx-empty">Nobody matches.</p> : null}
              </div>
            </div>
          </div>
        </section>
        <aside className="cx-side">
          {person ? (
            <PersonPanel view={view} person={person} onClose={() => setOpen(null)} />
          ) : (
            <section className="cx-panel cx-panel--padded">
              <h2 className="cx-panel__title">Press a person</h2>
              <p className="muted">to change their teams, see everything they can reach and why, or take it all away.</p>
            </section>
          )}
        </aside>
      </div>
      <HiddenPeople view={view} />
    </div>
  )
}
